<template>
    <div class="statistics-chart-applications" ref="statistics-chart-applications">
        <v-chart v-bind="{ options }" class="statistics-chart-applications__chart" :key="key" />
    </div>
</template>

<script>
import "echarts";
import dayjs from "@/plugins/vue-dayjs";
import VChart from "vue-echarts";

const reducer_method = ({ min, max, interval }, value, index, array) => {
    if (value < min) {
        min = value;
    }

    if (max < value) {
        max = value;
    }

    const spread = max - min;
    const unit = spread < 5 ? 1 : spread < 10 ? 2 : spread < 25 ? 5 : spread < 50 ? 10 : spread < 100 ? 20 : spread < 250 ? 50 : spread < 500 ? 100 : 200;

    if (array.length - 1 == index) {
        min = (Math.ceil(min / unit) - 1) * unit;
        if (min < 0) min = 0;

        max = (Math.floor(max / unit) + 1) * unit;

        interval = max / 5;
        if (interval < unit) interval = unit;
        else if (interval % unit) interval = Math.floor(interval / unit) * unit;
    }

    return { min, max, interval };
};
const reducer_object = { min: 0, max: 0, interval: 0 };

export default {
    components: {
        VChart,
    },
    props: {
        statistics: { type: Array, default: () => [] },
        statistics_visit: { type: Array, default: () => [] },

        searchDateValue: { type: Array, default: () => [] },
    },
    data: () => ({
        key: 0,

        width: undefined,
    }),
    computed: {
        hasVisits() {
            return 0 < this.statistics_visit.length;
        },

        xAxis_data() {
            let [startAt, endAt] = this.searchDateValue;

            if (!startAt || !endAt) {
                let statistics = [...this.statistics.map(({ _id }) => _id).sort()];

                if (!startAt) startAt = statistics.shift() ?? Date.now().toDate();
                if (!endAt) endAt = statistics.pop() ?? Date.now().toDate();

                if (this.hasVisits) {
                    statistics = [...this.statistics_visit.map(({ _id }) => _id).sort()];

                    let __startAt = statistics.shift() ?? Date.now().toDate();
                    if (this.$dayjs(__startAt).isBefore(startAt)) startAt = __startAt;

                    let __endAt = statistics.pop() ?? Date.now().toDate();
                    if (this.$dayjs(__endAt).isAfter(endAt)) endAt = __endAt;
                }
            }

            let data = [startAt.toDate()];
            do {
                data.push(dayjs(data.at(-1)).add(1, "day").toISOString().toDate());
            } while (dayjs(data.at(-1)).isBefore(endAt, "date"));

            return data;
        },

        xAxis_label_interval() {
            let count = this.xAxis_data.length;

            if (count < 5) return 1;
            if (count < 10) return 2;
            if (count < 15) return 3;
            if (count < 32) return 7;
            if (count < 184) return 30;
            if (count < 367) return 60;

            return 365;
        },

        yAxis_data_base() {
            return this.xAxis_data.map((date) => ({
                date,
                count: 0,
                ...this.statistics.find(({ _id }) => _id == date),
            }));
        },
        yAxis_data() {
            return this.yAxis_data_base.map(({ count }) => count);
        },
        yAxis_conf() {
            return this.yAxis_data.reduce(reducer_method, reducer_object);
        },

        yAxis_data_base__visit() {
            return this.xAxis_data.map((date) => ({
                date,
                users: 0,
                pages: 0,
                os: [],
                ...this.statistics_visit.find(({ _id }) => _id == date),
            }));
        },

        yAxis_data__visit_pages() {
            return this.yAxis_data_base__visit.map(({ pages }) => pages);
        },
        yAxis_conf__visit_pages() {
            return this.yAxis_data__visit_pages.reduce(reducer_method, reducer_object);
        },

        visit_os_items() {
            let items = this.statistics_visit.reduce((items, item) => {
                for (const { name } of item?.os || []) {
                    if (!items.includes(name)) items.push(name);
                }
                return items;
            }, []);

            return items.sort();
        },

        yAxis_datas__visit_os() {
            let names = this.visit_os_items;
            return this.yAxis_data_base__visit.reduce(
                (object, item) => {
                    for (const key of names) {
                        object[key].push(item.os.find(({ name }) => name == key)?.pages || 0);
                    }
                    return object;
                },
                names.reduce((object, key) => ({ ...object, [key]: [] }), [])
            );
        },

        legend_data() {
            let items = ["지원자수"];
            if (this.hasVisits) {
                items.push("페이지뷰", ...this.visit_os_items);
            }
            return items;
        },

        options() {
            return {
                legend: {
                    top: 8,
                    data: this.legend_data,
                },
                tooltip: {
                    trigger: "axis",
                },
                grid: [
                    {
                        top: 40,
                        left: "center",
                        height: 472 - 40 - 1 - 28,
                        width: this.width - 88,
                    },
                ],
                xAxis: [
                    {
                        type: "category",
                        data: this.xAxis_data,
                        axisLabel: {
                            interval: this.xAxis_label_interval,
                            formatter(value) {
                                return dayjs(value).format("M월 D일");
                            },
                        },
                    },
                ],
                yAxis: [
                    {
                        type: "value",
                        name: "지원자수",
                        ...this.yAxis_conf,
                    },

                    ...(this.hasVisits
                        ? [
                              {
                                  type: "value",
                                  name: "페이지뷰",
                                  ...this.yAxis_conf__visit_pages,
                              },
                          ]
                        : []),
                ],
                series: [
                    {
                        name: "지원자수",
                        type: "line",
                        yAxisIndex: 0,
                        animation: false,
                        data: this.yAxis_data,
                        smooth: true,
                    },

                    ...(this.hasVisits
                        ? [
                              {
                                  name: "페이지뷰",
                                  type: "line",
                                  yAxisIndex: 1,
                                  animation: false,
                                  data: this.yAxis_data__visit_pages,
                                  smooth: true,
                              },
                          ]
                        : []),

                    ...this.visit_os_items.map((name) => ({
                        name,
                        type: "line",
                        yAxisIndex: 1,
                        animation: false,
                        data: this.yAxis_datas__visit_os[name] || [],
                        smooth: true,
                    })),
                ],
            };
        },
    },
    methods: {
        init() {
            window.addEventListener("resize", this.calc);
        },
        calc() {
            const intervalId = setInterval(() => {
                const $el = this.$refs["statistics-chart-applications"];
                if (!$el) return;
                this.width = $el.getBoundingClientRect()?.width;
                clearInterval(intervalId);
            }, 10);
        },
        unddo() {
            window.removeEventListener("resize", this.calc);
        },
    },
    watch: {
        statistics() {
            this.key += 1;
        },
        width() {
            this.key += 1;
        },
    },
    mounted() {
        this.init();
        this.calc();
    },
};
</script>

<style lang="scss" scoped>
.statistics-chart-applications {
    &__chart {
        height: 472px;
        width: 100%;
        overflow: hidden;
    }
}
</style>